import {
  ActionGroup,
  Column,
  getParsedCrudData,
  getTranslatedText,
  Loader,
  preFormatForm,
  validateForm,
  advancedSearchConstraints
} from '@nile/components';
import { provisionModalValidation } from '@nile/components/src/Validators/forms/Contracts.js';
import {
  CREATE_CLIENT_PROVISION_RULE,
  CREATE_TEMPLATE_PROVISION_RULE,
  UPDATE_CLIENT_PROVISION_RULE
} from '@nile/graphql/src/Mutation/contracts';
import { GET_PROVISION_RULE } from '@nile/graphql/src/Query/contracts';
import { Modal, Page, TextField } from '@superops/ui-design-system';
import { pick } from 'lodash';
import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDeliveryMapContext } from '../../Contexts/DeliveryMapContext';
import { ProvisionRuleBuilder } from './ProvisionRuleBuilder';
import { ServiceItemTable } from './ServiceItemTable';
import { ENTITY_TYPE, PROVISION_RULES_ACTIONS } from '../../Constants';
import { AccountClient } from '@nile/graphql';
import { ProvisionRule } from '@nile/types';
import { useActivityTracker } from '@nile/components';

interface ModalProps {
  onClose: ProvisionRuleModalProps['onClose'];
  open: ProvisionRuleModalProps['open'];
  rule: ProvisionRule;
}

interface ProvisionRuleModalProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  groupName: string;
  setGroupName: (name: string) => void;
  primaryAction: () => void;
  ruleBuilderConditions: any;
  updateRuleBuilderConditions: (conditions: any) => void;
  servicecatalogItems: any[];
  setServiceCatalogItems: (items: any[]) => void;
  ruleEntityType: ENTITY_TYPE;
  provisionRuleId: string;
  isLoading?: boolean;
}

type ProvisionRuleModalRef = { validate: () => boolean };

export const ProvisionRuleModal = forwardRef<
  ProvisionRuleModalRef,
  ProvisionRuleModalProps
>(function ProvisionRuleModal(
  {
    open,
    onClose,
    title,
    groupName,
    setGroupName,
    primaryAction,
    ruleBuilderConditions,
    updateRuleBuilderConditions,
    servicecatalogItems,
    setServiceCatalogItems,
    ruleEntityType,
    provisionRuleId,
    isLoading
  },
  ref
) {
  const intl = useIntl();

  const [errors, setErrors] = useState(null);

  const [saveActionLoading, setSaveActionLoading] = useState(false);

  const { setIsDeliveryMapLoading } = useDeliveryMapContext();

  function validate() {
    const validationResult = validateForm(preFormatForm({ groupName }), {
      groupName: provisionModalValidation.groupName
    });

    const validateRuleBuilder = validateForm(
      ruleBuilderConditions,
      advancedSearchConstraints
    );

    if (validationResult || validateRuleBuilder) {
      setErrors({
        ...validationResult,
        ruleBuilderConditions: validateRuleBuilder?.operands
      });
      return true;
    }
    setErrors(null);
    return false;
  }

  async function handlePrimaryAction() {
    setIsDeliveryMapLoading(true);
    setSaveActionLoading(true);

    try {
      await primaryAction();
    } catch (e) {
      console.error(e);
    } finally {
      setIsDeliveryMapLoading(false);
      setSaveActionLoading(false);
    }
  }

  useImperativeHandle(ref, () => ({ validate }));

  return (
    /* @ts-ignore */
    <Modal large open={open} onClose={onClose} showCloseButton={false}>
      <Page
        variant="primary"
        contentHeight="100px"
        /* @ts-ignore */
        title={title}
        primaryAction={
          <div className="flex center">
            <ActionGroup
              secondaryAction={onClose}
              primaryAction={handlePrimaryAction}
              primaryLoading={saveActionLoading}
            />
          </div>
        }
      >
        {isLoading ? (
          <Loader />
        ) : (
          <Column gap={40}>
            {/* @ts-ignore */}
            <TextField
              value={groupName}
              onChange={(val) => setGroupName(val)}
              type="text"
              placeholder={getTranslatedText(
                intl,
                'msp.contracts.addNodeModal.placeholder'
              )}
              error={errors?.groupName}
            />
            <ProvisionRuleBuilder
              updateCondition={updateRuleBuilderConditions}
              conditions={ruleBuilderConditions}
              ruleEntityType={ruleEntityType}
              errors={errors?.ruleBuilderConditions}
            />
            <ServiceItemTable
              serviceItems={servicecatalogItems}
              setServiceCatalogItems={setServiceCatalogItems}
            />
          </Column>
        )}
      </Page>
    </Modal>
  );
});

export const AddProvisionRuleModal = withModalWrapper(
  function AddProvisionRuleModal({ onClose, open, rule }: ModalProps) {
    const [groupName, setGroupName] = useState('');
    const [ruleBuilderConditions, setRuleBuilderConditions] = useState({
      joinOperator: 'AND',
      operands: []
    });
    const [servicecatalogItems, setServiceCatalogItems] = useState([]);

    const { ruleEntityType, provisionRuleId, name } = rule;

    const {
      client,
      dispatchProvisionRulesList,
      isTemplate,
      template
    } = useDeliveryMapContext();

    const formRef = useRef<{ validate: () => boolean }>();

    const intl = useIntl();

    const [createProvisionRule] = useMutation(CREATE_CLIENT_PROVISION_RULE, {
      client: AccountClient
    });

    const [createTemplateProvisionRule] = useMutation(
      CREATE_TEMPLATE_PROVISION_RULE,
      {
        client: AccountClient
      }
    );

    async function onCreateProvisionRule() {
      const hasError = formRef.current?.validate();
      if (hasError) return;

      try {
        const resp = await (isTemplate
          ? createTemplateProvisionRule
          : createProvisionRule)({
          variables: {
            input: {
              ...(isTemplate
                ? {
                    template: {
                      templateId: template?.templateId
                    }
                  }
                : {
                    client: {
                      accountId: client?.accountId
                    }
                  }),
              provisionRule: {
                name: groupName,
                ruleEntityType,
                ruleCondition: ruleBuilderConditions,
                addItems: servicecatalogItems?.map((item) =>
                  pick(item, 'itemId')
                ),
                parentRule: {
                  provisionRuleId
                }
              }
            }
          }
        });
        useActivityTracker({
          event: 'Created a new delivery group'
        });
        dispatchProvisionRulesList({
          type: PROVISION_RULES_ACTIONS.ADD_PROVISION_RULE,
          payload: {
            provisionRuleData:
              resp.data?.[
                isTemplate
                  ? 'createProvisionRuleTemplate'
                  : 'createClientProvisionRule'
              ]
          }
        });
        return true;
      } catch {
      } finally {
        onClose();
      }
    }

    return (
      <ProvisionRuleModal
        ref={formRef}
        onClose={onClose}
        open={open}
        primaryAction={onCreateProvisionRule}
        title={
          <FormattedMessage
            id="msp.contract.addNodeTitle"
            values={{ parentTitle: getTranslatedText(intl, name) }}
          />
        }
        groupName={groupName}
        setGroupName={setGroupName}
        ruleBuilderConditions={ruleBuilderConditions}
        updateRuleBuilderConditions={setRuleBuilderConditions}
        servicecatalogItems={servicecatalogItems}
        setServiceCatalogItems={setServiceCatalogItems}
        ruleEntityType={ruleEntityType as ENTITY_TYPE}
        provisionRuleId={provisionRuleId}
      />
    );
  }
);

export const EditProvisionRuleModal = withModalWrapper(
  function EditProvisionRuleModal({ onClose, open, rule }: ModalProps) {
    const [groupName, setGroupName] = useState('');
    const [ruleBuilderConditions, setRuleBuilderConditions] = useState({
      joinOperator: 'AND',
      operands: []
    });
    const [servicecatalogItems, setServiceCatalogItems] = useState([]);

    const intl = useIntl();

    const { ruleEntityType, provisionRuleId, parentRule, name } = rule;

    const formRef = useRef<{ validate: () => boolean }>();

    const {
      client,
      dispatchProvisionRulesList,
      isTemplate
    } = useDeliveryMapContext();

    const {
      loading: getProvisionRuleLoading,
      data: provisionRuleData
    } = useQuery(GET_PROVISION_RULE, {
      onCompleted(resp) {
        if (resp?.getProvisionRule) {
          setServiceCatalogItems(resp.getProvisionRule.items);
          setRuleBuilderConditions(resp.getProvisionRule.ruleCondition);
          setGroupName(resp.getProvisionRule.name);
        }
      },
      fetchPolicy: 'no-cache',
      client: AccountClient,
      variables: {
        input: {
          provisionRuleId,
          name
        },
        isClient: !isTemplate
      }
    });

    const [updateProvisionRule] = useMutation(UPDATE_CLIENT_PROVISION_RULE, {
      client: AccountClient,
      variables: {
        isClient: !isTemplate
      }
    });

    async function onEditProvisionRule() {
      const hasError = formRef.current?.validate();
      if (hasError) return;

      const { addedItems, deletedItems } = getParsedCrudData(
        provisionRuleData?.getProvisionRule?.items?.map((item) =>
          pick(item, 'itemId')
        ),
        servicecatalogItems?.map((item) => pick(item, 'itemId')),
        'itemId'
      );

      try {
        const resp = await updateProvisionRule({
          variables: {
            /* @ts-ignore */
            input: {
              provisionRuleId,
              name: groupName,
              ruleCondition: ruleBuilderConditions,
              addItems: addedItems,
              deleteItems: deletedItems
            }
          }
        });
        if (addedItems?.length) {
          useActivityTracker({
            event: 'Add services to group'
          });
        }
        dispatchProvisionRulesList({
          type: PROVISION_RULES_ACTIONS.UPDATE_PROVISION_RULE,
          payload: {
            provisionRuleId,
            provisionRuleData: resp.data?.updateProvisionRule
          }
        });
        return true;
      } catch {
      } finally {
        onClose();
      }
    }

    // if (getProvisionRuleLoading) return <Loader />;

    return (
      <ProvisionRuleModal
        ref={formRef}
        isLoading={getProvisionRuleLoading}
        onClose={onClose}
        open={open}
        primaryAction={onEditProvisionRule}
        title={
          <FormattedMessage
            id="msp.contract.editNodeTitle"
            values={{ parentTitle: getTranslatedText(intl, parentRule?.name) }}
          />
        }
        groupName={groupName}
        setGroupName={setGroupName}
        ruleBuilderConditions={ruleBuilderConditions}
        updateRuleBuilderConditions={setRuleBuilderConditions}
        servicecatalogItems={servicecatalogItems}
        setServiceCatalogItems={setServiceCatalogItems}
        ruleEntityType={ruleEntityType as ENTITY_TYPE}
        provisionRuleId={provisionRuleId}
      />
    );
  }
);

export function withModalWrapper(Component: (arg: any) => JSX.Element) {
  return function ModalWraper(props: any) {
    if (!props.open) return null;
    return <Component {...props} />;
  };
}
